/**
 * DateRangePicker.tsx — compact range picker for the filter panel's
 * "between" date operator (Part 5.5d A6).
 *
 * Decision: built as a separate primitive instead of adding a range
 * mode to DateTimePicker. The two share the day-grid + month/year drill
 * concept but diverge meaningfully — DateTimePicker has time row +
 * preset chips + 460px two-pane footprint; range needs neither and
 * benefits from a tighter 300px single-pane layout. Sharing internals
 * would have meant either a heavy conditional in DateTimePicker or
 * exporting a bunch of helpers that don't have other callers yet.
 *
 * Tradeoff accepted: some helper duplication (buildDayGrid, yearRangeFor,
 * sameDay) between the two pickers. Both files stay readable; if a third
 * date primitive appears, those helpers come out into a shared util.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { CalendarMonth as CalendarIcon, Close as ClearIcon } from '@mui/icons-material';
import { HUB, FONT_BODY } from '../../clients/hub/designSystem';
import { PopoverPortal, usePopoverPosition } from '../InlineEdit/popoverPortal';
import styles from './DateRangePicker.module.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

type DrillLevel = 'day' | 'month' | 'year';

// ── Helpers ──────────────────────────────────────────────────────────────

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(d: Date, start: Date, end: Date): boolean {
  const t = d.getTime();
  return t > start.getTime() && t < end.getTime();
}

function buildDayGrid(year: number, month: number): Date[] {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay();
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - startOffset);
  const out: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(d);
  }
  return out;
}

function yearRangeFor(year: number): { start: number; end: number; years: number[] } {
  const start = year - (year % 12);
  const end = start + 11;
  const years: number[] = [];
  for (let i = 0; i < 12; i++) years.push(start + i);
  return { start, end, years };
}

function fmtIso(d: Date | null): string {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parse a YYYY-MM-DD value to the start of that LOCAL calendar day.
 *
 *  A bare date string is parsed as UTC midnight by the spec, so west of
 *  Greenwich `new Date('2026-09-08')` lands on Sep 7 once read back with
 *  local getters — the picker then highlighted, and the trigger then
 *  printed, the day BEFORE the one the user clicked. Writing was never
 *  wrong (`fmtIso` uses local getters), so the range drifted a day earlier
 *  on every read-back of a value it had just written correctly.
 *
 *  Anchoring the bare form to local midnight is the same fix, for the same
 *  reason, as `DateTimePicker.parseBound`. */
function parseIso(s: string | undefined | null): Date | null {
  if (!s) return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return startOfDay(d);
}

function fmtDisplay(d: Date | null): string {
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Inline icons ─────────────────────────────────────────────────────────

const ChevLeft: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevRight: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ── Props ────────────────────────────────────────────────────────────────

export interface DateRangePickerProps {
  /** Start of the range as YYYY-MM-DD or undefined. */
  from?: string;
  /** End of the range as YYYY-MM-DD or undefined. */
  to?: string;
  /** Receives the new range on Save. from/to are YYYY-MM-DD strings (or
   *  undefined when cleared). */
  onSave: (next: { from?: string; to?: string }) => void;
  onCancel: () => void;
  /** 'save' (default) keeps the explicit Cancel/Save footer and commits
   *  only on Save. 'live' commits on each completed range change and shows
   *  a Done affordance instead — the TimeReports pattern. */
  commitMode?: 'save' | 'live';
}

// ── Component ────────────────────────────────────────────────────────────

const DateRangePicker: React.FC<DateRangePickerProps> = ({ from, to, onSave, onCancel, commitMode = 'save' }) => {
  const today = useMemo(() => startOfDay(new Date()), []);

  const initialStart = parseIso(from);
  const initialEnd = parseIso(to);

  const [start, setStart] = useState<Date | null>(initialStart);
  const [end, setEnd] = useState<Date | null>(initialEnd);
  // Hover-preview day while the second click is still pending.
  const [hover, setHover] = useState<Date | null>(null);

  // View tracks the calendar month being shown.
  const [viewYear, setViewYear] = useState<number>(
    (initialStart ?? today).getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState<number>(
    (initialStart ?? today).getMonth(),
  );
  const [drill, setDrill] = useState<DrillLevel>('day');

  // Re-init when from/to props change (rare in normal usage but defensive).
  useEffect(() => {
    setStart(parseIso(from));
    setEnd(parseIso(to));
  }, [from, to]);

  const onDayClick = useCallback(
    (d: Date) => {
      const dayD = startOfDay(d);
      let nextStart: Date | null;
      let nextEnd: Date | null;
      if (!start || (start && end)) {
        // Beginning a new range.
        nextStart = dayD;
        nextEnd = null;
      } else {
        // Completing the range. Order so start <= end.
        if (dayD.getTime() < start.getTime()) {
          nextStart = dayD;
          nextEnd = start;
        } else {
          nextStart = start;
          nextEnd = dayD;
        }
      }
      setStart(nextStart);
      setEnd(nextEnd);
      setHover(null);
      // Live mode: commit as soon as a full range is formed.
      if (commitMode === 'live' && nextStart && nextEnd) {
        onSave({ from: fmtIso(nextStart), to: fmtIso(nextEnd) });
      }
    },
    [start, end, commitMode, onSave],
  );

  const headerText =
    drill === 'day'
      ? `${MONTH_NAMES[viewMonth]} ${viewYear}`
      : drill === 'month'
        ? String(viewYear)
        : (() => {
            const { start: s, end: e } = yearRangeFor(viewYear);
            return `${s}–${e}`;
          })();

  const onHeaderClick = () => {
    if (drill === 'day') setDrill('month');
    else if (drill === 'month') setDrill('year');
  };

  const onNavPrev = () => {
    if (drill === 'day') {
      let m = viewMonth - 1;
      let y = viewYear;
      if (m < 0) { m = 11; y -= 1; }
      setViewMonth(m); setViewYear(y);
    } else if (drill === 'month') {
      setViewYear((y) => y - 1);
    } else {
      setViewYear((y) => y - 12);
    }
  };
  const onNavNext = () => {
    if (drill === 'day') {
      let m = viewMonth + 1;
      let y = viewYear;
      if (m > 11) { m = 0; y += 1; }
      setViewMonth(m); setViewYear(y);
    } else if (drill === 'month') {
      setViewYear((y) => y + 1);
    } else {
      setViewYear((y) => y + 12);
    }
  };

  const dayGrid = useMemo(() => buildDayGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const yearRange = yearRangeFor(viewYear);

  const handleSave = () => {
    onSave({ from: start ? fmtIso(start) : undefined, to: end ? fmtIso(end) : undefined });
  };

  const handleClear = () => {
    setStart(null);
    setEnd(null);
    onSave({ from: undefined, to: undefined });
  };

  // Range bounds — convenient for the grid CSS classes.
  const lo = start ?? end;
  const hi = end ?? start;

  // Hover preview: only when a start is set, the end isn't, and the hovered
  // day is after the start. Highlights the tentative range lightly.
  const previewEnd =
    start && !end && hover && hover.getTime() > start.getTime() ? hover : null;

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <button
          type="button"
          className={styles.headLabel}
          onClick={onHeaderClick}
          disabled={drill === 'year'}
        >
          {headerText}
        </button>
        <div className={styles.nav}>
          <button type="button" className={styles.navBtn} onClick={onNavPrev} aria-label="Previous">
            <ChevLeft />
          </button>
          <button type="button" className={styles.navBtn} onClick={onNavNext} aria-label="Next">
            <ChevRight />
          </button>
        </div>
      </div>

      {drill === 'day' && (
        <div className={styles.dayGrid}>
          {DOW.map((d, i) => (
            <div key={`dow-${i}`} className={styles.dow}>{d}</div>
          ))}
          {dayGrid.map((d, i) => {
            const inMonth = d.getMonth() === viewMonth;
            const isToday = sameDay(d, today);
            const isStart = lo && hi && sameDay(d, lo);
            const isEnd = lo && hi && sameDay(d, hi);
            const isMiddle = lo && hi && isBetween(d, lo, hi);
            const isPreviewEnd = previewEnd && sameDay(d, previewEnd);
            const isPreviewMiddle = start && previewEnd && isBetween(d, start, previewEnd);
            const cls = [
              styles.day,
              !inMonth ? styles.muted : '',
              isToday ? styles.today : '',
              (isStart || isEnd) ? styles.rangeEdge : '',
              isStart && !isEnd ? styles.rangeStart : '',
              isEnd && !isStart ? styles.rangeEnd : '',
              isMiddle ? styles.rangeMiddle : '',
              isPreviewMiddle ? styles.previewMiddle : '',
              isPreviewEnd ? styles.previewEnd : '',
            ].filter(Boolean).join(' ');
            return (
              <button
                key={`day-${i}`}
                type="button"
                className={cls}
                onClick={() => onDayClick(d)}
                onMouseEnter={() => { if (start && !end) setHover(startOfDay(d)); }}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      )}

      {drill === 'month' && (
        <div className={styles.monthGrid}>
          {MONTH_SHORT.map((m, idx) => {
            const isToday = today.getFullYear() === viewYear && today.getMonth() === idx;
            const isSelected = !!start && start.getFullYear() === viewYear && start.getMonth() === idx;
            const cls = [styles.cell, isToday ? styles.today : '', isSelected ? styles.selected : '']
              .filter(Boolean).join(' ');
            return (
              <button
                key={m}
                type="button"
                className={cls}
                onClick={() => { setViewMonth(idx); setDrill('day'); }}
              >
                {m}
              </button>
            );
          })}
        </div>
      )}

      {drill === 'year' && (
        <div className={styles.yearGrid}>
          {yearRange.years.map((y) => {
            const isToday = today.getFullYear() === y;
            const isSelected = !!start && start.getFullYear() === y;
            const cls = [styles.cell, isToday ? styles.today : '', isSelected ? styles.selected : '']
              .filter(Boolean).join(' ');
            return (
              <button
                key={y}
                type="button"
                className={cls}
                onClick={() => { setViewYear(y); setDrill('month'); }}
              >
                {y}
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.foot}>
        <span className={styles.rangeText}>
          {fmtDisplay(start)} → {fmtDisplay(end)}
        </span>
        <div className={styles.actions}>
          <button type="button" className={styles.btn} onClick={handleClear}>Clear</button>
          {commitMode === 'live' ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={onCancel}
            >
              Done
            </button>
          ) : (
            <>
              <button type="button" className={styles.btn} onClick={onCancel}>Cancel</button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleSave}
                disabled={!start || !end}
              >
                Save
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── RangeField ─────────────────────────────────────────────────────────
// HubDateField-style labeled trigger for the range picker: uppercase
// micro-label + 4px hairline trigger showing 'Jul 1 – Jul 15, 2026', opens
// DateRangePicker in a PopoverPortal. The labeled field-shaped sibling of
// HubDateField for range surfaces (TimeReports, board filters).

function fmtRangeDisplay(startIso: string | null, endIso: string | null): string {
  const s = parseIso(startIso ?? undefined);
  const e = parseIso(endIso ?? undefined);
  if (!s && !e) return '';
  const noYear = { month: 'short', day: 'numeric' } as const;
  const withYear = { month: 'short', day: 'numeric', year: 'numeric' } as const;
  if (s && e) {
    // Same year: only the end carries the year ('Jul 1 – Jul 15, 2026').
    if (s.getFullYear() === e.getFullYear()) {
      return `${s.toLocaleDateString('en-US', noYear)} – ${e.toLocaleDateString('en-US', withYear)}`;
    }
    return `${s.toLocaleDateString('en-US', withYear)} – ${e.toLocaleDateString('en-US', withYear)}`;
  }
  if (s) return `${s.toLocaleDateString('en-US', withYear)} – …`;
  return `… – ${e!.toLocaleDateString('en-US', withYear)}`;
}

export const RangeField: React.FC<{
  label: string;
  /** Range start as YYYY-MM-DD, or null. */
  start: string | null;
  /** Range end as YYYY-MM-DD, or null. */
  end: string | null;
  onChange: (start: string | null, end: string | null) => void;
  clearable?: boolean;
  disabled?: boolean;
  minWidth?: number | string;
  commitMode?: 'save' | 'live';
  placeholder?: string;
}> = ({ label, start, end, onChange, clearable, disabled, minWidth = 220, commitMode = 'save', placeholder = 'Pick a range…' }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const coords = usePopoverPosition(anchorRef as any, open, 360);

  const display = fmtRangeDisplay(start, end);
  const hasValue = !!(start || end);

  return (
    <Box sx={{ minWidth }}>
      <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: HUB.inkFaint, fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        ref={anchorRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={() => { if (!disabled) setOpen(true); }}
        onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); } }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          border: '1px solid', borderColor: open ? HUB.indigo : HUB.line,
          borderWidth: open ? 1.5 : 1,
          borderRadius: '4px', px: 1.25, py: 0.9,
          cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
          bgcolor: disabled ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.15s ease',
          '&:hover': disabled ? {} : { borderColor: HUB.indigo },
        }}
      >
        <CalendarIcon sx={{ fontSize: 16, color: hasValue ? HUB.indigo : HUB.muted }} />
        <Typography sx={{ fontSize: 13.5, fontFamily: FONT_BODY, color: hasValue ? 'text.primary' : HUB.muted, flex: 1, fontWeight: hasValue ? 600 : 400 }}>
          {display || placeholder}
        </Typography>
        {clearable && hasValue && !disabled && (
          <IconButton
            aria-label={`Clear ${label}`}
            size="small"
            onClick={e => { e.stopPropagation(); onChange(null, null); }}
            sx={{ p: 0.25, mr: -0.5 }}
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      <PopoverPortal open={open} coords={coords} onClickOutside={() => setOpen(false)}>
        <DateRangePicker
          from={start ?? undefined}
          to={end ?? undefined}
          commitMode={commitMode}
          onSave={next => {
            onChange(next.from ?? null, next.to ?? null);
            if (commitMode !== 'live') setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      </PopoverPortal>
    </Box>
  );
};

export default DateRangePicker;
